import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DicesIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PencilRulerIcon, PersonStandingIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/vb4HJMYoPtth2QGnrR3p96/mcp_07d2f251-33b2-43dd-a200-2f4ed63932d4_1792081803858.webp';
export const heroGuarantees = ['Omedelbar digital nedladdning', 'Engångsbetalning', 'Ingen prenumeration', '7 dagars nöjdhetsgaranti'];
export const stats = [{
  value: '300',
  label: 'Sidor i huvudvolymerna'
}, {
  value: '6',
  label: 'Digitala idrottsvolymer'
}, {
  value: '9',
  label: 'Årskurser – från 1 till 9'
}, {
  value: '5',
  label: 'Bonusar som ingår'
}, {
  value: '1',
  label: 'Komplett undervisningssystem'
}];
export const differenceStats = [{
  value: 'Årskurs 1–9',
  label: 'Material organiserat i tre stadier'
}, {
  value: '6 digitala volymer',
  label: 'Från motorik och rörelseglädje till träning, friluftsliv och hälsa'
}, {
  value: 'Utskrivbara PDF-filer',
  label: 'Att använda digitalt eller skriva ut till undervisningen'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Söndagskvällarna försvinner',
  text: 'Timmar framför datorn för att planera nästa veckas lektioner – och du är fortfarande inte klar.'
}, {
  title: 'Timmar av letande',
  text: 'Övningar från olika webbplatser är för lätta, för svåra eller måste anpassas innan de går att använda.'
}, {
  title: 'Arbetsbladen skapas från grunden',
  text: 'Instruktioner, illustrationer, upplägg och lärarstöd – det som skulle ta minuter blir en lång arbetsuppgift.'
}, {
  title: 'Svårt att göra rörelserna visuella',
  text: 'Rörelsesekvenser, stationsbilder och spelplaner tar tid att ta fram för varje nytt moment.'
}, {
  title: 'Bedömning och uppföljning tar tid',
  text: 'Uppgifter, observationsunderlag och uppföljning av elevernas utveckling blir ännu mer arbete efter skoltid.'
}, {
  title: 'Stress inför nästa lektion',
  text: 'När tiden inte räcker till blir det lätt att improvisera och få ihop material i sista minuten.'
}];
export const afterItems = ['Ett organiserat idrottspaket för hela grundskolan', 'Lektionsupplägg som är enkla att använda', 'Färdiga rörelseövningar', 'Arbetsblad som kan skrivas ut direkt', 'Visuella förklaringar och rörelsebilder', 'Aktiviteter för repetition och färdighetsträning', 'Rörelsespel som skapar variation', 'Bedömningsmaterial och lärarstöd', 'Planeringsstöd för hela läsåret'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Söndagarna går åt till planering',
  text: 'Lägger du delar av helgen på att skapa idrottslektioner inför kommande vecka?'
}, {
  icon: FolderSearchIcon,
  title: 'Du vet inte vilket arbetsblad du ska använda',
  text: 'Letar du ständigt efter övningar som passar elevernas nivå?'
}, {
  icon: LightbulbIcon,
  title: 'Du vill ha tydligare förklaringar',
  text: 'Behöver du mer visuellt material för att förklara rörelser och aktiviteter på ett enkelt sätt?'
}, {
  icon: PencilRulerIcon,
  title: 'Du vill slippa skapa allt från noll',
  text: 'Önskar du att lektioner, uppgifter och aktiviteter redan var förberedda?'
}, {
  icon: ShuffleIcon,
  title: 'Eleverna behöver mer variation',
  text: 'Vill du använda fler spel, utmaningar och praktiska aktiviteter i undervisningen?'
}, {
  icon: LayoutGridIcon,
  title: 'Du behöver en tydligare struktur',
  text: 'Vill du ha materialet samlat så att du snabbt hittar det du behöver?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volym 01',
  level: 'Åk 1–3',
  title: 'Lek, motorik och rörelseglädje',
  tagline: 'Ge eleverna en trygg grund i idrott och hälsa.',
  intro: 'Den första volymen fokuserar på grundläggande motorik och rörelse genom lek.',
  items: ['Grundläggande rörelser', 'Löpning och hopp', 'Balans och koordination', 'Kasta och fånga', 'Visuella rörelseövningar', 'Samarbetsövningar', 'Utskrivbara arbetsblad', 'Rörelselekar', 'Repetition och bedömning'],
  outro: 'Färdigt material som gör grundläggande rörelse konkret och begriplig.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 02',
  level: 'Åk 1–3',
  title: 'Utelek, vattenvana och hälsa',
  tagline: 'Låt eleverna upptäcka rörelse både inomhus, utomhus och i vatten.',
  intro: 'Den andra volymen innehåller praktiska aktiviteter för utelek, vattenvana och de första stegen mot goda hälsovanor.',
  items: ['Utelekar och rörelse i naturen', 'Allemansrätten för de yngsta', 'Vattenvana och trygghet i vatten', 'Säkerhet vid vatten', 'Kost, sömn och vila', 'Rörelse och välmående', 'Praktiska aktiviteter', 'Visuella arbetsblad', 'Repetition och bedömning'],
  outro: 'Färdiga övningar där eleverna får leka, utforska och må bra.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 03',
  level: 'Åk 4–6',
  title: 'Spel, rörelse och samarbete',
  tagline: 'Utveckla elevernas spelförståelse och samarbetsförmåga.',
  intro: 'Den tredje volymen hjälper eleverna att utveckla teknik, taktik och samarbete i bollspel, lekar och rörelse till musik.',
  items: ['Bollspel och regler', 'Passningar och mottagningar', 'Spelförståelse och taktik', 'Rörelse till musik och dans', 'Redskapsgymnastik', 'Samarbete och fair play', 'Visuella spelplaner', 'Rörelsespel', 'Utskrivbara arbetsblad', 'Bedömningar och lärarstöd'],
  outro: 'Tydliga exempel och aktiviteter för idrottsundervisningen i årskurs 4–6.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 04',
  level: 'Åk 4–6',
  title: 'Friluftsliv, simning och hälsa',
  tagline: 'Ge eleverna kunskaper för ett aktivt liv utomhus och i vatten.',
  intro: 'Den fjärde volymen kombinerar friluftsliv, orientering och simning med praktiska aktiviteter om hälsa och livsstil.',
  items: ['Friluftsliv och allemansrätten', 'Orientering och kartkunskap', 'Simning och simsätt', 'Säkerhet vid vatten och is', 'Hälsa och livsstil', 'Kost och träning', 'Utomhusaktiviteter', 'Stationsupplägg', 'Rörelsespel', 'Bedömningsuppgifter'],
  outro: 'Ett praktiskt stöd för undervisning utanför idrottshallen.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 05',
  level: 'Åk 7–9',
  title: 'Idrott, träning och rörelse',
  tagline: 'Hjälp eleverna att förstå hur träning påverkar kroppen.',
  intro: 'Den femte volymen innehåller färdiga aktiviteter för att arbeta med träningslära, kondition, styrka och komplexa rörelser.',
  items: ['Träningslära och träningsprinciper', 'Kondition och uthållighet', 'Styrka och rörlighet', 'Uppvärmning och nedvarvning', 'Komplexa rörelser', 'Bollspel och taktik', 'Dans och rörelse till musik', 'Egna träningsprogram', 'Ergonomi och skadeförebyggande', 'Repetition och bedömning'],
  outro: 'Visuella och strukturerade resurser för idrottsundervisningen på högstadiet.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 06',
  level: 'Åk 7–9',
  title: 'Friluftsliv, hälsa och livräddning',
  tagline: 'Låt eleverna fördjupa sina kunskaper om hälsa och säkerhet.',
  intro: 'Den sjätte volymen innehåller friluftsliv, hälsa, livsstil och livräddning med övningar för både teori och praktik.',
  items: ['Friluftsliv under olika årstider', 'Orientering i okänd terräng', 'Livräddning och första hjälpen', 'HLR och nödsituationer', 'Simkunnighet och säkerhet', 'Hälsa, sömn och stress', 'Kost och livsstil', 'Resonemang om hälsa', 'Praktiska uppgifter', 'Bedömningsstöd och lärarstöd'],
  outro: 'Ett komplett komplement för friluftsliv, hälsa och säkerhet.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitala volymer för årskurs 1–9',
  text: 'Ett komplett idrottspaket som hjälper dig att planera och genomföra undervisningen under hela läsåret. Varje volym innehåller 50 sidor med idrottsmaterial, övningar och praktiska aktiviteter.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Färdiga lektioner',
  text: 'Slipp börja från ett tomt dokument. Tydliga lektionsupplägg, exempel och aktiviteter hjälper dig att komma igång snabbare.'
}, {
  icon: FileTextIcon,
  title: 'Utskrivbara arbetsblad',
  text: 'Elevuppgifter som kan skrivas ut och användas direkt – för självständigt arbete, repetition och extra träning.'
}, {
  icon: PersonStandingIcon,
  title: 'Visuella rörelseövningar',
  text: 'Rörelsesekvenser, stationsbilder, spelplaner och illustrationer gör övningarna mer konkreta och lättare att förstå.'
}, {
  icon: DicesIcon,
  title: 'Rörelsespel och aktiviteter',
  text: 'Spel, utmaningar och praktiska övningar ger eleverna fler sätt att träna och använda sina kunskaper.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Bedömningar och lärarstöd',
  text: 'Färdiga bedömningsuppgifter och observationsunderlag som stöd för bedömning och uppföljning.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplanering',
  text: 'Stöd för att organisera idrottsundervisningen och välja arbetsområden utifrån din planering.'
}];
export const pageSamples: Array<{src: string; label: string; title: string; text: string}> = [{
  src: 'https://cdn.magicpatterns.com/uploads/q3j5PWqGDReKWpWR6KBNyx/Captura-de-pantalla-2026-10-15-a-las-3.43.59_p._m..webp',
  label: 'Lektion 1 · Sida 24',
  title: 'Passportaler',
  text: 'En färdig lektion med steg-för-steg-bilder, passningsbana, materiallista och fokus för lärarens observation.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/bpvkGEawakeKGDyGAPXWYt/Captura-de-pantalla-2026-10-15-a-las-3.44.22_p._m..webp',
  label: 'Lektion 5 · Sida 14',
  title: 'Bollkontroll i bana',
  text: 'En visuell slalombana där eleverna rullar, studsar och för bollen – med tydlig lärarobservation.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nULrJC4ZxFdWWvTqUD1WgP/Captura-de-pantalla-2026-10-15-a-las-3.45.07_p._m..webp',
  label: 'Elevblad 8 · Sida 21',
  title: 'Stationscirkel för hela gruppen',
  text: 'Ett utskrivbart elevblad med fyra stationer, lugn paus och plats för elevens reflektion och nästa steg.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/f8bdA2odiu1qVB6niDuwMT/Captura-de-pantalla-2026-10-15-a-las-3.45.22_p._m..webp',
  label: 'Lektion 12 · Sida 28',
  title: 'Rörelseutmaningen',
  text: 'Fyra stationer för passning, bana, sekvens och rytm – med en färdig tidsplan för hela lektionen.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/5372GxHbubtBaJVvx7cexy/Captura-de-pantalla-2026-10-15-a-las-3.46.20_p._m..webp',
  label: 'Lärarstöd · Sida 46',
  title: 'Plan B med samma mål',
  text: 'Färdiga anpassningar för mindre yta, få bollar, kortare tid eller svårt ljud – utan att tappa lektionens mål.'
}];
export const pageTypes = ['Färdiga lektioner', 'Utskrivbara arbetsblad', 'Praktiska aktiviteter', 'Bedömningar och lärarstöd'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Vikariepaketet',
  text: 'Organisera instruktioner, aktiviteter och lektionsmål för årskurs 1–9 och få bättre struktur när en vikarie tar över.',
  items: ['Färdiga lektionsupplägg', 'Tydliga aktivitetsinstruktioner', 'Lektionsmål och materiallistor', 'Praktiska överlämningsmallar'],
  benefit: 'Mindre tid på planering och mer tid till undervisning.'
}, {
  title: 'Rörelsepauser på 5 minuter',
  text: 'En extra samling korta rörelseaktiviteter för olika årskurser och undervisningssituationer.',
  items: ['Extra rörelseövningar', 'Koordinationsövningar', 'Rytm och variation', 'Aktiviteter på olika nivåer'],
  benefit: 'Perfekt när du snabbt behöver fler övningar.'
}, {
  title: 'Bildstöd för idrottshallen',
  text: 'Bildstöd och aktivitetskort som hjälper eleverna att förstå instruktioner och delta i undervisningen.',
  items: ['Utskrivbara bildkort', 'Visuella instruktioner', 'Aktivitetskort', 'Stöd för gemensamma rutiner'],
  benefit: 'Mer variation i undervisningen utan att du behöver skapa bildstödet själv.'
}, {
  title: 'Idrottsdagen – steg för steg',
  text: 'Kompletterande material för att planera en idrottsdag och få överblick över aktiviteter, stationer och genomförande.',
  items: ['Stationsupplägg', 'Praktiska checklistor', 'Underlag för uppföljning', 'Instruktioner och reservplaner'],
  benefit: 'Ett praktiskt stöd för arbetet med idrottsdagen.'
}, {
  title: 'Kommunikationspaket för hem och skola',
  text: 'Mallar och praktiska resurser som hjälper dig att kommunicera om idrottsundervisningen.',
  items: ['Informationsmallar', 'Tydliga instruktioner', 'Förslag på meddelanden till hemmet', 'Mallar för olika situationer'],
  benefit: 'Känn dig bättre förberedd inför varje idrottslektion.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Öppna IDROTTSKLAR™',
  text: 'Ladda ner paketet och öppna volymen som passar din årskurs.'
}, {
  title: 'Välj lektion och material',
  text: 'Hitta rätt arbetsområde och välj de övningar, arbetsblad eller aktiviteter som passar dina elever.'
}, {
  title: 'Förbered dig på 5 minuter',
  text: 'Gå igenom instruktionerna och välj det material du behöver.'
}, {
  title: 'Skriv ut och undervisa',
  text: 'Skriv ut arbetsbladen eller använd materialet digitalt – och gå in i idrottshallen med en tydlig plan.'
}];
export const audienceItems = ['Undervisar i idrott och hälsa i årskurs 1–3, 4–6 eller 7–9', 'Vill ha ett helt läsårs idrottsmaterial organiserat', 'Vill slippa skapa varje arbetsblad från grunden', 'Behöver fler visuella rörelseövningar', 'Vill använda rörelsespel och praktiska aktiviteter', 'Vill ha enkel tillgång till bedömningsmaterial och lärarstöd', 'Vill spara tid på veckoplaneringen', 'Vill känna sig bättre förberedd inför undervisningen'];
export const timeValueTasks = ['Leta efter arbetsblad', 'Skriva övningar', 'Skapa visuella förklaringar', 'Förbereda aktiviteter', 'Bedöma och följa upp elevernas arbete'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitala volymer – 300 sidor',
  detail: 'Motorik, rörelse, spel, samarbete, friluftsliv och hälsa'
}, {
  label: '5 bonusar utan extra kostnad',
  detail: 'Vikariepaket, rörelsepauser, bildstöd, idrottsdagsplanering och kommunikationsmallar'
}, {
  label: 'För årskurs 1–9',
  detail: 'Färdiga lektioner, arbetsblad och lärarstöd'
}];
export const offerIncludes = ['Omedelbar digital nedladdning', 'PDF-filer att spara', 'Utskrivbara arbetsblad', 'Ingen fysisk produkt skickas'];
export const PRICE = '299\u00A0kr';

// Only add authentic reviews of IDROTTSKLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hur får jag materialet?',
  a: 'IDROTTSKLAR™ levereras digitalt. Efter köpet får du tillgång till PDF-filer som du kan ladda ner och spara. Du kan använda materialet på dator, surfplatta eller mobil och skriva ut de arbetsblad du behöver.'
}, {
  q: 'Är IDROTTSKLAR™ anpassat för den svenska skolan?',
  a: 'Ja. Materialet är framtaget för undervisning i idrott och hälsa i den svenska grundskolan, med utgångspunkt i det centrala innehållet i Lgr22. Det är ett fristående undervisningsmaterial och inte en officiell produkt från Skolverket.'
}, {
  q: 'Passar det för årskurs 1–9?',
  a: 'Ja. De sex volymerna är organiserade i tre stadier – årskurs 1–3, 4–6 och 7–9. Du väljer material utifrån den årskurs och det arbetsområde du undervisar i.'
}, {
  q: 'Kan jag verkligen förbereda en lektion på 5 minuter?',
  a: 'IDROTTSKLAR™ är utformat för att göra lektionsförberedelserna snabbare. Du kan öppna en volym, välja ett färdigt lektionsupplägg eller arbetsblad och gå igenom det på ungefär fem minuter. Tiden kan variera beroende på lektionsinnehåll, elevgrupp och behov av anpassning.'
}, {
  q: 'Räcker materialet för ett helt läsår?',
  a: 'IDROTTSKLAR™ innehåller material och aktiviteter som kan användas under hela läsåret. Du väljer, kombinerar och anpassar resurserna efter din årsplanering och elevernas behov.'
}, {
  q: 'Finns det färdiga arbetsblad?',
  a: 'Ja. Materialet innehåller utskrivbara arbetsblad med uppgifter, rörelseövningar och visuella förklaringar.'
}, {
  q: 'Ingår lärarstöd?',
  a: 'Ja. Paketet innehåller lärarstöd, instruktioner och observationsunderlag som stöd för genomförande, bedömning och uppföljning.'
}, {
  q: 'Kan jag använda materialet för elever på olika nivåer?',
  a: 'Ja. IDROTTSKLAR™ innehåller varierade övningar och aktiviteter som du kan välja och anpassa utifrån elevernas förutsättningar och behov.'
}, {
  q: 'Måste jag skriva ut allt?',
  a: 'Nej. Du väljer själv vilka sidor du skriver ut, och du kan även använda delar av materialet digitalt.'
}, {
  q: 'Ingår de fem bonusarna?',
  a: `Ja. Alla fem bonusar ingår i paketet utan extra kostnad – allt för ${PRICE}.`
}, {
  q: 'Är det en prenumeration?',
  a: 'Nej. Du betalar en gång och får tillgång till det digitala materialet. Inga återkommande avgifter.'
}, {
  q: 'Kan jag spara filerna?',
  a: 'Ja. Du kan ladda ner och spara PDF-filerna för fortsatt användning enligt produktens användarvillkor.'
}, {
  q: 'Kan jag använda materialet på mobil eller surfplatta?',
  a: 'Ja. PDF-filerna kan öppnas på dator, surfplatta och mobil. För att granska layouten på arbetsbladen före utskrift rekommenderar vi dator eller surfplatta.'
}, {
  q: 'Finns det någon garanti?',
  a: 'Ja. IDROTTSKLAR™ har 7 dagars nöjdhetsgaranti. Om materialet inte motsvarar dina förväntningar kan du kontakta oss och begära återbetalning inom garantiperioden enligt villkoren.'
}];
export const footerLinks = ['Köpvillkor', 'Integritetspolicy', 'Återbetalningspolicy', 'Kontakt'];
